import {
  ActionReducerMapBuilder,
  createAction,
  createReducer,
} from "@reduxjs/toolkit";

import { getActionDispatcherWithPayload } from "@marta/utils";

import {
  AcknowledgeMessage,
  AddConnectedUser,
  AddMessage,
  AddTypingUser,
  ConnectedUsers,
  Messages,
  MessagesState,
  Rooms,
  TypingUsers,
  initialAppState,
} from "./message-types";

export const setMessages = createAction<Messages>("setMessages");
export const useMessagesSetter = getActionDispatcherWithPayload(setMessages);

export const addMessage = createAction<AddMessage>("addMessage");
export const useAddMessageSetter = getActionDispatcherWithPayload(addMessage);

export const acknowledgeMessage =
  createAction<AcknowledgeMessage>("acknowledgeMessage");
export const useAcknowledgeMessageSetter =
  getActionDispatcherWithPayload(acknowledgeMessage);

export const setRooms = createAction<Rooms>("setRooms");
export const useRoomsSetter = getActionDispatcherWithPayload(setRooms);

export const setNewMessagesCount = createAction<number>("setNewMessagesCount");
export const useNewMessagesCountSetter =
  getActionDispatcherWithPayload(setNewMessagesCount);

export const resetNewMessagesCount = createAction("resetNewMessagesCount");
export const useNewMessagesCountResetter = getActionDispatcherWithPayload(
  resetNewMessagesCount,
);

export const addNewMessagesCount = createAction<null>("addNewMessagesCount");
export const useNewMessagesCountAdder =
  getActionDispatcherWithPayload(addNewMessagesCount);

export const setTyping = createAction<TypingUsers>("setTyping");
export const useTypingSetter = getActionDispatcherWithPayload(setTyping);

export const addTypingUser = createAction<AddTypingUser>("addTypingUser");
export const useAddTypingUserSetter =
  getActionDispatcherWithPayload(addTypingUser);

export const removeTypingUser = createAction<AddTypingUser>("removeTypingUser");
export const useRemoveTypingUserSetter =
  getActionDispatcherWithPayload(removeTypingUser);

export const setConnected = createAction<ConnectedUsers>("setConnected");
export const useConnectedSetter = getActionDispatcherWithPayload(setConnected);

export const addConnectedUser =
  createAction<AddConnectedUser>("addConnectedUser");
export const useAddConnectedUserSetter =
  getActionDispatcherWithPayload(addConnectedUser);

export const createSlice = <State>(options: {
  initialState: State;
  reducers?: (builder: ActionReducerMapBuilder<State & MessagesState>) => void;
}) => {
  const reducer = createReducer(
    Object.assign(initialAppState, options.initialState),
    (builder) => {
      builder
        .addCase(setMessages, (state, action) => {
          state.messages = action.payload;
        })
        .addCase(addMessage, (state, action) => {
          const modified = [
            ...(state.messages[action.payload.roomId] || []),
            action.payload.message,
          ];
          state.messages = {
            ...state.messages,
            [action.payload.roomId]: modified.sort((a, b) => {
              return (
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
              );
            }),
          };
        })
        .addCase(acknowledgeMessage, (state, action) => {
          state.messages = {
            ...state.messages,
            [action.payload.roomId]: state.messages[action.payload.roomId].map(
              (m) =>
                m.id === action.payload.messageId ? { ...m, read: true } : m,
            ),
          };
        })
        .addCase(setRooms, (state, action) => {
          state.rooms = action.payload;
        })
        .addCase(setTyping, (state, action) => {
          state.typing = action.payload;
        })
        .addCase(setNewMessagesCount, (state, action) => {
          state.newMessages = action.payload;
        })
        .addCase(resetNewMessagesCount, (state) => {
          state.newMessages = 0;
        })
        .addCase(addNewMessagesCount, (state) => {
          state.newMessages = state.newMessages + 1;
        })
        .addCase(addTypingUser, (state, action) => {
          state.typing = {
            ...state.typing,
            [action.payload.roomId]: [
              ...(state.typing[action.payload.roomId]
                ? state.typing[action.payload.roomId]
                : []),
              action.payload.name,
            ],
          };
        })
        .addCase(removeTypingUser, (state, action) => {
          state.typing = {
            ...state.typing,
            [action.payload.roomId]: state.typing[action.payload.roomId]
              ? state.typing[action.payload.roomId].filter(
                  (name) => name !== action.payload.name,
                )
              : [],
          };
        })
        .addCase(setConnected, (state, action) => {
          state.connected = action.payload;
        })
        .addCase(addConnectedUser, (state, action) => {
          state.connected = {
            ...state.connected,
            [action.payload.roomId]: [
              ...state.connected[action.payload.roomId],
              action.payload.name,
            ],
          };
        });

      if (options?.reducers) options.reducers(builder);
    },
  );

  return {
    reducer,
    getInitialState: reducer.getInitialState,
    actions: {
      setMessages,
      addMessage,
      setRooms,
      setTyping,
      addTypingUser,
      setConnected,
      addConnectedUser,
    },
  };
};
